import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import SpinnerSSE from "./SpinnerSSE.jsx";
import sseFinish from "../img/sse_finish.png";
import sseLoading from "../img/sse_loading.png";
import dottedLine from "../img/dotted_line.png";
import useStore from "../store.js";

/* 반응형 Breakpoint는 판례 특징 요약(CaseSummarySection.jsx)과 동일 */
const appearAnimWithY = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px) scale(0.98);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const appearAnimWithoutY = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.98);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const SSEProgressStack = ({ showCompleteText }) => {
  const { progressSteps } = useStore();
  const [animatingId, setAnimatingId] = useState(null);
  const listWrapperRef = useRef(null);
  const previousMaxOrder = useRef(0);

  useEffect(() => {
    if (listWrapperRef.current) {
      listWrapperRef.current.scrollTo({
        top: listWrapperRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
    const maxOrder = progressSteps.reduce(
      (maximum, step) => Math.max(maximum, step.order),
      0,
    );
    if (previousMaxOrder.current > 0 && maxOrder > previousMaxOrder.current) {
      const latest = progressSteps.find((step) => step.order === maxOrder);
      setAnimatingId(latest?.id || null);
      const timer = window.setTimeout(() => setAnimatingId(null), 3800);
      previousMaxOrder.current = maxOrder;
      return () => window.clearTimeout(timer);
    }
    previousMaxOrder.current = maxOrder;
    return undefined;
  }, [progressSteps]);

  const renderWaitingWithSituation = () => (
    <WaitingSituationWrapper>
      <WaitingBox>
        <WaitingHeader>
          <SpinnerTextWrapper>
            {showCompleteText ? (
              <>
                <FinishIcon src={sseFinish} alt="완료" />
                <WaitingText>판례 검색이 완료되었습니다</WaitingText>
              </>
            ) : (
              <>
                <SpinnerSSE />
                <WaitingText>결과를 처리 중입니다</WaitingText>
              </>
            )}
          </SpinnerTextWrapper>
          {!showCompleteText && (
            <WaitingHelpText>
              검색이 끝나면 결과가 열려요. 새로고침해도 같은 검색을 이어서 확인해요.
            </WaitingHelpText>
          )}
        </WaitingHeader>
        <StyledHr />
        <ContentListWrapper $stepCount={progressSteps.length} ref={listWrapperRef}>
          {progressSteps.map((item) => {
            const isDone = showCompleteText || item.state === "completed";
            return (
              <ContentBoxStyled
                key={item.id}
                className={animatingId === item.id ? "appear" : ""}
                $stepCount={progressSteps.length}
              >
                <IconBox>
                  <Img
                    src={isDone ? sseFinish : sseLoading}
                    alt={isDone ? "완료" : "로딩중"}
                  />
                </IconBox>
                <ContentTextBox>
                  <ContentTitle $active={isDone}>
                    <span className="step">Step {item.order}. </span>
                    <span className="desc">{item.title}</span>
                  </ContentTitle>
                  <ContentBody>{item.content}</ContentBody>
                </ContentTextBox>
              </ContentBoxStyled>
            );
          })}
        </ContentListWrapper>
      </WaitingBox>
    </WaitingSituationWrapper>
  );

  return renderWaitingWithSituation();
};

const WaitingSituationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WaitingBox = styled.div`
  width: 680px;
  height: 381px;
  border-radius: 24px;
  background: #F9F9F9;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-top: 24px;
  box-sizing: border-box;

  @media (max-width: 744px) {
    width: calc(100% - 64px);
    height : 408px;
  }

  @media (max-width: 480px) and (min-width: 320px) {
    width: calc(100% - 48px);
    height : 424px;
  }
`;

const WaitingHeader = styled.div`
  width: 100%;
  padding: 24px 25px 21px;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 18px 24px 21px;
  }
`;

const SpinnerTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const StyledHr = styled.hr`
  width: 100%;
  border: none;
  border-top: 0.3px solid rgba(0, 0, 0, 0.10);
  margin: 0;
`;

const ContentListWrapper = styled.div`
  position: relative;
  width: 94%; /* 스크롤 위치 조정을 위해 94%로 설정 */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-top: ${({ $stepCount }) => ($stepCount <= 2 ? "24px" : "0px")}; /* step 3부터는 점선이 위에 닿도록 */
  margin-left: 32px;
  margin-bottom: 32px;
  padding-right: 47px; /* Step 내용 오른쪽 여백*/
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overscroll-behavior: contain;
  background-image: url(${dottedLine});
  background-repeat: repeat-y;
  background-position: 8px -28px; /* 점선 겹침 현상으로 인해 -28px로 설정 */
  background-size: 2px auto;

  /* 스크롤바 커스텀 */
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-button {
    display: none;
    height: 0;
    width: 0;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.05) transparent;


  @media (max-width: 744px) {
    width: 91%;
    margin-left: 24px;
    padding-right: 23px;
  }

  @media (max-width: 480px) and (min-width: 320px) {
    width: 91%;
    margin-top: ${({ $stepCount }) => ($stepCount <= 2 ? "20px" : "0px")}; /* step 3부터는 점선이 위에 닿도록 */
    margin-left: 18px;
    padding-right: 19px; /* 스크롤의 width가 6px임을 감안 */
    background-position: 14px -24px;
  }
`;

const ContentBoxStyled = styled.div`
  position: relative;
  min-height: 106px;
  margin-bottom: 12px;
  border: none;
  border-radius: 12px;
  background: transparent;
  display: flex;
  align-items: flex-start;
  // padding-top: 23px;
  // padding-left: 32px;
  box-sizing: border-box;
  box-shadow: none;
  &.appear {
    animation: ${({ $stepCount }) => 
      $stepCount <= 2 ? appearAnimWithoutY : appearAnimWithY
    } 2.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  @media (max-width: 744px) {
    min-height: auto;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) and (min-width: 320px) {
    min-height: auto;
    margin-bottom: 20px;
    margin-left: 5px; /* 체크 이미지가 점선 위에 맞추기 위함 */
  }
`;

const IconBox = styled.div`
  width: 20px;
  height: 20px;
  margin-right: 20px;
  display: flex;
  align-items: flex-start;

  @media (max-width: 480px) and (min-width: 320px) {
    margin-right: 10px;
  }
`;

const Img = styled.img`
  width: inherit;
  height: inherit;
`;

const ContentTextBox = styled.div``;

const ContentTitle = styled.h3`
  color: ${({ $active }) => $active ? 'rgba(0, 0, 0, 0.60)' : 'transparent'};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: -0.32px;
  margin:0;
  margin-bottom: 8px;

  ${({ $active }) => !$active && `
    background: linear-gradient(
      120deg,
      rgba(180, 180, 180, 0.15) 0%,
      rgba(80, 80, 80, 0.85) 50%,
      rgba(180, 180, 180, 0.15) 100%
    );
    background-size: 200% auto;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
    animation: shimmer 2s infinite linear;
  `}

  @keyframes shimmer {
    0% {
      background-position: 100% 100%;
    }
    100% {
      background-position: -100% 0;
    }
  }


 @media (max-width: 480px) and (min-width: 320px) {
  font-size: 14px;
  letter-spacing: -0.28px;
  }
`;

const ContentBody = styled.p`
  color: rgba(0, 0, 0, 0.60);
  text-align: justify;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; 
  letter-spacing: -0.28px;
  margin:0;

  @media (max-width: 480px) and (min-width: 320px) {
    color: rgba(0, 0, 0, 0.40);
    font-size: 13px;
    font-weight: 500;
    line-height: 23px; 
    letter-spacing: -0.26px;
  }
`;

const FinishIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 8px;
`;

const WaitingText = styled.p`
  /* 모든 디바이스 동일*/
  color: rgba(0, 0, 0, 0.60);
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px; 
  letter-spacing: -0.32px;
  margin:0;
`;

const WaitingHelpText = styled.p`
  color: rgba(0, 0, 0, 0.60);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.28px;
  margin: 8px 0 0 29px;
  word-break: keep-all;
  overflow-wrap: anywhere;
`;

export default SSEProgressStack;
